import {
  EEventState,
  eChannels,
  eInternalEvents,
  ePublishers,
  eTopics
} from '../utils/enums';
import {
  WIDGET_API_TYPE_COMBINATION,
  WIDGET_API_UPDATE_APPEND
} from '../utils/constants';
import {
  extendSportClientSession,
  updateUserContext
} from '../redux/user/user-actions';
import { getKey, getKeyByValue } from '../parse-categories/get-menu-mapping';
import {
  getMockDefinitionChannels,
  publishTopicOnChannel,
  subscribeToTopic
} from '../services/external-pub-sub/pub-sub-utils';

import DP from './data-provider';
import GTMLayer from './gtm-layer-facade';
import Global from '../global';
import InternalLinkLib from '../components/shared/internal-link-lib';
import InternalPubSub from '../services/internal-pub-sub/index';
import PubSub from '../services/external-pub-sub';
import SessionBL from './session-facade';
import WidgetApi from '../services/kambi-widget-api';
import actions from '../redux/user/user-actions-creator';
import { enums } from '@888sport/uc-sportclient-lib/dist/uc-sportclient-lib.min';
import { parse } from 'url';
import { runInitialActions } from '../client-rendering';
import { setNativeUXVersion } from '../redux/app-ui/app-ui-actions';

const { eEventLogType, eSources } = enums;

// import internalLinkLib from '../components/shared/internal-link-lib';

function exposeInitFunction(store) {
  window.SportClient = window.SportClient || {};

  window.SportClient.init = options => {
    const {
      GermanyRegulationEnabled,
      NewBannerEnabled,
      deviceType,
      languageCode
    } = window.uc.getClientSettings() || {};

    if (options && options.reportingParameters) {
      Global.logger.info(
        `Reporting parameters received: ${JSON.stringify(
          options.reportingParameters
        )} when SportClientSDK = ${JSON.stringify(window.SportClientSDK)}`
      );
      const {
        globalSessionId,
        playerSessionId,
        productPackage,
        parentProductPackage,
        cid
      } = options.reportingParameters;

      const { contentLanguage } = options;

      //update user context
      store.dispatch(
        updateUserContext({
          cid,
          globalSessionId,
          playerSessionId,
          productPackage,
          parentProductPackage,
          contentLanguage,
          GermanyRegulationEnabled,
          NewBannerEnabled,
          deviceType,
          languageCode
        })
      );
      GTMLayer.setGTMStaticData({
        eventHeader: {
          userData: {
            cid
          }
        },
        sessionData: {
          globalSessionId,
          playerSessionId
        },
        clientData: {
          productPackage,
          parentProductPackage
        }
      });
    }

    if (options && options.nativeUXVersion) {
      InternalPubSub.publishEvent(
        eInternalEvents.NativeUXVersion,
        options.nativeUXVersion
      );
    }

    let eventData = {
      type: eEventLogType.WrapperChannels,
      source: eSources.client
    };

    if (options && options.messageBrokerChannels) {
      eventData.found = true;
      PubSub.set(options.messageBrokerChannels);
      InternalPubSub.publishEvent(eInternalEvents.ChannelsSentByWrapper);

      ucDataStoreReady()
      .then(
          () => publishTopicOnChannel(
            eTopics.session.subscribedToMessageBroker,
            eChannels.session,
            'SportClient.init();',
            null,
            { publisher: ePublishers.sportclient }
          ))      
      .catch(console.error); //eslint-disable-line      
    } else {
      eventData.found = false;
    }    
  };
}

function subscribeToWrapperEvents(store) {
  //TODO Heartbeat conditional in session-manager
  const cbSessionHeartbeat = (data, envelope) => {
    const state = store.getState();
    const { userContext } = state;

    const dataToLog = data ? JSON.stringify(data) : 'no';
    const envelopeToLog = envelope ? JSON.stringify(envelope) : 'no';
    Global.logger.debug(
      `MESSAGE RECEIVED ***  session channel | heartBeat topic | ${dataToLog} data | ${envelopeToLog} envelope`
    );

    if (!envelope || !envelope.headers || !envelope.headers.publisher) {
      Global.logger.warn(
        'MESSAGE FORMAT ERROR *** session channel | heartBeat topic - STOPPED PROCESSING'
      );
      return;
    }

    if (envelope.headers.publisher === ePublishers.sportwrapper) {
      if (userContext.isLoggedIn) {
        store.dispatch(extendSportClientSession(userContext));
      }
      SessionBL.reportUserActivityToSportProvider();
    }
  };

  const cbIdentityAuthenticate = data => {
    Global.logger.log('Got identity authenticate', data);
    const { currency } = store.getState().userContext;

    store.dispatch(
      updateUserContext({
        cid: data && data.CID,
        currency: data.Currency || currency
      })
    );

    runInitialActions(store, data && data.Token);
  };

  const cbPlayerSegmentation = data => {
    const {
      country,
      isFTD,
      isVIP,
      currency,
      device,
      isPromotionPermitted,
      isPromotionRestricted,
      languageCode,
      RMS,
      userMode,
      serial,
      reportingParameters: {
        cid,
        globalSessionId,
        playerSessionId,
        productPackage,
        parentProductPackage
      }
    } = data;

    GTMLayer.setGTMStaticData({
      eventHeader: {
        userData: {
          cid
        }
      },
      sessionData: {
        globalSessionId,
        playerSessionId
      },
      clientData: {
        productPackage,
        parentProductPackage
      }
    });

    store.dispatch(
      updateUserContext({
        cid,
        country,
        globalSessionId,
        isFTD,
        isVIP,
        parentProductPackage,
        playerSessionId,
        productPackage,
        currency,
        device,
        isPromotionPermitted,
        isPromotionRestricted,
        languageCode,
        RMS,
        userMode,
        serial
      })
    );
  };

  const cbIdentityLogout = data => {
    Global.logger.log('Got identity logout', data);

    store.dispatch(actions.logoutSuccess());
    WidgetApi.get().logout();
    window._kc.ticket = null;
  };

  const cbNavigate = ({ uri }) => {
    Global.logger.log(`[SC] Navigate to: ${uri}`);
    Global.logger.log('[SC] Navigate. Seeking for a kambi url...');
    const { langISO2, contentLanguage } = store.getState().userContext;
    const { mappings } = store.getState().menu;

    const parsedUrl = parse(uri);

    const kambiUrl = getKey(
      parsedUrl && parsedUrl.pathname.replace(`/${contentLanguage}/`, '/'),
      mappings,
      langISO2
    );

    const wapi = WidgetApi.get();
    if (!wapi) {
      Global.logger.log(
        `[SC] Navigate Kambi client to: ${uri} failed because WAPI is not loaded yet.`
      );

      return;
    }

    const hash =
      parsedUrl.hash || (kambiUrl && `#${kambiUrl}`) || window.location.hash;
    parsedUrl.search = window.location.search;

    if (
      parsedUrl.hash === window.location.hash &&
      parsedUrl.pathname === window.location.pathname
    ) {
      InternalLinkLib.replaceInternalRoute(hash.replace('#', ''));
      wapi.navigateClient(hash);
      return;
    }

    if (hash) {
      parsedUrl.hash = undefined;
      window.history.pushState({}, document.title, parsedUrl.format());
      parsedUrl.hash = hash;
      InternalLinkLib.replaceInternalRoute(hash.replace('#', ''));
    } else if (
      parsedUrl &&
      parsedUrl.pathname &&
      parsedUrl.pathname !== window.location.pathname
    ) {
      window.history.pushState({}, document.title, parsedUrl.format());
    }

    // if (parsedUrl && parsedUrl.hash) {
    //   Global.logger.log(`[SC] Navigate Kambi client to: ${parsedUrl.hash}`);
    //   // InternalLinkLib.goToInternalRoute(
    //   //   (parsedUrl.hash || '').replace('#', '')
    //   // );
    //   wapi.navigateClient(parsedUrl.hash);
    // } else if (kambiUrl) {
    //   Global.logger.log(`[SC] Navigate Kambi client to: #${kambiUrl}`);
    //   // InternalLinkLib.goToInternalRoute(kambiUrl);
    //   wapi.navigateClient(`#${kambiUrl}`);
    // } else Global.logger.log("[SC] Navigate. Didn't find any matches...");
  };

  const cbShowBetslip = data => {
    const { show } = data || { show: true };
    Global.logger.log('Got player showBetslip', data);

    WidgetApi.get().showBetslip(show);
  };

  const cbAddToBetslip = async ({ betSlipData } = {}) => {
    let navigationUrl = '/filter/football';
    Global.logger.log('Got player addToBetslip', betSlipData);
    const { selectionIds } = betSlipData || {};
    WidgetApi.get().setOutcomesToBetSlip(
      selectionIds,
      WIDGET_API_UPDATE_APPEND,
      WIDGET_API_TYPE_COMBINATION
    );

    if (Array.isArray(selectionIds) && selectionIds.length === 1) {
      const {
        userContext,
        appConfig: { useProdApis }
      } = store.getState();
      const outcome = await DP.getBetOfferOutcome(
        selectionIds[0],
        userContext,
        useProdApis
      );

      if (outcome && Array.isArray(outcome.events) && outcome.events.length) {
        const eventId = outcome.events[0].id;

        navigationUrl =
          event.state === EEventState.STARTED
            ? `/event/live/${eventId}`
            : `/event/${eventId}`;
      }
    }

    const { langISO2 } = store.getState().userContext;
    const { mappings } = store.getState().menu;

    const pathname =
      getKeyByValue('/filter/football', mappings, langISO2) || '/';

    const { navigation } = eChannels;
    const {
      navigation: { changeBrowserURI }
    } = eTopics;

    InternalLinkLib.goToInternalRoute(navigationUrl);

    const newUrl = `${pathname}#${navigationUrl}`;
    history.replaceState({}, document.title, newUrl);
    publishTopicOnChannel(
      changeBrowserURI,
      navigation,
      'cbAddToBetslip',
      {
        uri: newUrl
      },
      { publisher: 'cbAddToBetslip' }
    );

    publishTopicOnChannel(
      eTopics.session.clientInitSucceeded,
      eChannels.session,
      'Betslip click handler finished',
      null,
      { publisher: ePublishers.sportclient }
    );
  };

  InternalPubSub.subscribeToEvent(eInternalEvents.ChannelsSentByWrapper, () => {
    const { session, identity, player, navigation } = eChannels;
    const {
      identity: { authenticate, logout },
      navigation: { Navigate },
      player: { playerSegmentationUpdate, showBetslip, addToBetslip },
      session: { heartBeat }
    } = eTopics;

    subscribeToTopic(
      navigation,
      Navigate,
      cbNavigate,
      'WrapperMessageListeners'
    );

    subscribeToTopic(
      session,
      heartBeat,
      cbSessionHeartbeat,
      'WrapperMessageListeners'
    );
    subscribeToTopic(
      identity,
      authenticate,
      cbIdentityAuthenticate,
      'WrapperMessageListeners'
    );

    subscribeToTopic(
      player,
      playerSegmentationUpdate,
      cbPlayerSegmentation,
      'WrapperMessageListeners'
    );

    subscribeToTopic(
      identity,
      logout,
      cbIdentityLogout,
      'WrapperMessageListeners'
    );

    subscribeToTopic(
      player,
      showBetslip,
      cbShowBetslip,
      'WrapperMessageListeners'
    );

    subscribeToTopic(
      player,
      addToBetslip,
      cbAddToBetslip,
      'WrapperMessageListeners'
    );
  });

  InternalPubSub.subscribeToEvent(
    eInternalEvents.NativeUXVersion,
    nativeUXVersion => {
      store.dispatch(setNativeUXVersion(nativeUXVersion));
    }
  );
}

export function setCommunicationWithWrapper(store) {
  const state = store.getState();
  subscribeToWrapperEvents(store);

  let { isMockWrapper } = state.appConfig;
  exposeInitFunction(store);
  if (isMockWrapper) {
    window.SportClient.init(getMockDefinitionChannels());
  }
}

export const ucDataStoreReady = async () => {
  if (typeof window.uc.dataStoreReady !== 'undefined') {
      const isReady = await window.uc.dataStoreReady;

      return isReady;
  }

  return Promise.resolve();
};
